import type { NodeType } from './enums';

/**
 * Allowed parent types per node type (PRD 6.1). `null` means "no parent" is allowed.
 * Only an area with a parent, a section outside a project, a project inside a list and cycles are invalid
 * in spirit; this table is the precise rule set the API enforces.
 */
export const ALLOWED_PARENTS: Record<NodeType, readonly (NodeType | null)[]> = {
  area: [null],
  goal: ['area', 'goal', null],
  result: ['goal'],
  task: ['area', 'goal', 'result', 'task', 'project', 'section', 'list', null],
  idea: ['area', 'goal', 'result', 'task', 'idea', 'routine', 'project', 'section', 'list', null],
  routine: ['area', 'goal', 'result', 'project'],
  project: ['area', 'goal', 'result', null],
  section: ['project'],
  list: ['area', 'goal', null],
};

export function canHaveParent(childType: NodeType, parentType: NodeType | null): boolean {
  return ALLOWED_PARENTS[childType].includes(parentType);
}

export function parentRuleError(childType: NodeType, parentType: NodeType | null): string | null {
  if (canHaveParent(childType, parentType)) return null;
  const allowed = ALLOWED_PARENTS[childType].map((t) => t ?? 'none').join(', ');
  return `A ${childType} cannot have ${parentType ? `a ${parentType}` : 'no'} parent. Allowed parents: ${allowed}`;
}

/**
 * True if moving `nodeId` under `newParentId` would create a cycle.
 * `ancestors` are the ids from `newParentId` up to the root (including `newParentId` itself).
 */
export function wouldCreateCycle(nodeId: number, newParentId: number | null, ancestors: readonly number[]): boolean {
  if (newParentId === null) return false;
  if (newParentId === nodeId) return true;
  return ancestors.includes(nodeId);
}

export const IDEA_CONVERSION_TARGETS = ['task', 'goal', 'result'] as const satisfies readonly NodeType[];
export type IdeaConversionTarget = (typeof IDEA_CONVERSION_TARGETS)[number];

export interface ConvertibleNode {
  type: NodeType;
  status: string;
  converted_from_type: NodeType | null;
}

export interface ConversionPatch {
  type: IdeaConversionTarget;
  converted_from_type: NodeType;
  status?: 'active';
}

/**
 * Rules for converting an idea (PRD 6.1). The node keeps its id, notes and history; only type,
 * converted_from_type and (if it was still an idea) status change. Callers must still check the
 * parent rule for the new type.
 */
export function convertIdea(
  node: ConvertibleNode,
  targetType: NodeType,
  parentType: NodeType | null,
): { ok: true; patch: ConversionPatch } | { ok: false; error: string } {
  if (node.type !== 'idea') return { ok: false, error: `Only ideas can be converted, this is a ${node.type}` };
  if (!(IDEA_CONVERSION_TARGETS as readonly NodeType[]).includes(targetType)) {
    return { ok: false, error: `An idea can be converted to ${IDEA_CONVERSION_TARGETS.join(', ')}, not ${targetType}` };
  }
  const parentError = parentRuleError(targetType, parentType);
  if (parentError) return { ok: false, error: parentError };
  const patch: ConversionPatch = { type: targetType as IdeaConversionTarget, converted_from_type: 'idea' };
  if (node.status === 'idea') patch.status = 'active';
  return { ok: true, patch };
}
